/**
 * <s-hero-video> — Background hero video with a pause/play button.
 * Children: <video data-hero-video>, <button data-hero-toggle> (aria-pressed = paused).
 * Starts paused for prefers-reduced-motion; pauses while off screen.
 */
class SHeroVideo extends HTMLElement {
  #controller = null;
  #observer = null;
  #userPaused = false;

  connectedCallback() {
    const video = this.querySelector('[data-hero-video]');
    const toggle = this.querySelector('[data-hero-toggle]');
    if (!video) return;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const sync = () => toggle?.setAttribute('aria-pressed', String(video.paused));

    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#userPaused = true;
      video.pause();
    } else {
      video.play().catch(() => {});
    }

    toggle?.addEventListener('click', () => {
      if (video.paused) { this.#userPaused = false; video.play().catch(() => {}); }
      else { this.#userPaused = true; video.pause(); }
    }, { signal });
    video.addEventListener('play', sync, { signal });
    video.addEventListener('pause', sync, { signal });
    sync();

    this.#observer = new IntersectionObserver(([entry]) => {
      if (this.#userPaused) return;
      if (entry.isIntersecting) video.play().catch(() => {});
      else video.pause();
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }
}

if (!customElements.get('s-hero-video')) customElements.define('s-hero-video', SHeroVideo);
